import { skipToken } from '@reduxjs/toolkit/query';
import { Box, Message, ProgressBar, Text } from '@timelyapp/react';
import React from 'react';
import { ticApi } from '~redux/modules/tic/api-slice/tic.apiSlice';
import type { UseSyncRunProgressParams } from '../../hooks/useSyncRunProgress';
import { useSyncRunProgress } from '../../hooks/useSyncRunProgress';

const JOBS_POLLING_INTERVAL_MS = 5000;

export interface IntegrationsSharedSyncRunProgressBannerProps
  extends Pick<UseSyncRunProgressParams, 'providerId' | 'connectionId'> {
  /**
   * Fan-out framework providers report progress via sync_run; legacy providers
   * have no runs and fall back to a generic message while pg-boss jobs are active.
   */
  isFrameworkProvider: boolean;
}

/**
 * Sync activity for the connection, meant to sit above the route/step content so
 * it is visible on every view while a sync is in flight. Renders nothing when the
 * connection is idle.
 *
 * Framework providers show a progress bar over the active run's units. The bar's
 * total grows as the fan-out discovers work, so percent can stall or dip — the
 * count label is the monotonic signal. The generic jobs-based fallback is for
 * legacy providers ONLY: framework runs leave trailing jobs (diff generation,
 * event dispatch) that would otherwise keep a meaningless banner up after the
 * run completes.
 */
export const IntegrationsSharedSyncRunProgressBanner: React.FC<
  IntegrationsSharedSyncRunProgressBannerProps
> = ({ providerId, connectionId, isFrameworkProvider }) => {
  const progress = useSyncRunProgress({
    providerId,
    connectionId,
    enabled: isFrameworkProvider,
  });

  const { data: jobsData } = ticApi.useGetConnectionJobsQuery(
    !isFrameworkProvider && providerId && connectionId
      ? { provider: providerId, connectionId, includeRecent: false }
      : skipToken,
    { pollingInterval: JOBS_POLLING_INTERVAL_MS }
  );
  const hasRunningJobs = (jobsData?.data ?? []).some(
    job => job.state === 'created' || job.state === 'active' || job.state === 'retry'
  );

  if (isFrameworkProvider && progress.showProgress) {
    return (
      <Box.Column padding="large" style={{ paddingBottom: 0 }}>
        <Message variant="info" title="Sync in progress">
          <Box.Column gap="small">
            <ProgressBar
              animated
              indeterminate
              value={(progress.completed / progress.total) * 100}
              size="small"
            />
            <Text size="xsmall" color="subtle">
              {progress.completed} / {progress.total}
            </Text>
          </Box.Column>
        </Message>
      </Box.Column>
    );
  }

  if (!isFrameworkProvider && hasRunningJobs) {
    return (
      <Box.Column padding="large" style={{ paddingBottom: 0 }}>
        <Message variant="info" title="Sync in progress">
          <Text>A sync is currently running for this connection.</Text>
        </Message>
      </Box.Column>
    );
  }

  return null;
};
